//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import React, { useState } from 'react';
import { RegisterOptions, useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import { SAMPLING_CAMPAIGNS_TRACKING_ID } from '@campaigns/constants/sampling/SamplingCampaignsTrackingId';
import {
  isProductImageContentTypeValid,
  isProductImageUrlValid,
} from '@campaigns/constants/sampling/SamplingProductValidationRulesConstants';
import { EditableBVSamplingProduct } from '@campaigns/types/sampling/SamplingProductTypes';
import { DefaultButton } from '@components/atoms/button/Button';
import Icon from '@components/atoms/icon/Icon';
import { CaptionErrorText } from '@components/atoms/text/Text';
import { TextInput } from '@components/atoms/text-input/TextInput';
import { FormFieldController } from '@components/form/field-controller/FormFieldController';

export interface UrlTabProps {
  index: number;
  onFileSelect: (file: File | undefined) => void;
  onPreview: () => void;
  rules:
    | RegisterOptions<EditableBVSamplingProduct, `variations.${number}.imgUrl`>
    | undefined;
}

export const UrlTab = ({
  index,
  onFileSelect,
  onPreview,
  rules,
}: UrlTabProps) => {
  const { control, watch } = useFormContext<EditableBVSamplingProduct>();
  const [errorMsg, setErrorMsg] = useState<string>('');
  const currentValue = watch(`variations.${index}.imgUrl`);

  const handleAdd = async () => {
    if (!currentValue) {
      return;
    }
    if (!isProductImageUrlValid(currentValue)) {
      setErrorMsg('Please enter a valid URL');
      return;
    }
    const isValid = await isProductImageContentTypeValid(currentValue);
    if (!isValid) {
      setErrorMsg('Invalid product image URL or unsupported format');
      return;
    }
    // Clear any previously uploaded file so the entered URL isn't overwritten on submit
    onFileSelect(undefined);
    onPreview();
  };

  return (
    <Container>
      <FormFieldController
        control={control}
        name={`variations.${index}.imgUrl`}
        rules={rules}
      >
        {({ error, invalid, onChange, ...fieldProps }) => (
          <InputRow>
            <StyledTextInput
              {...fieldProps}
              aria-label='Variant image URL'
              errorText={error?.message}
              hasError={invalid}
              onChange={(value) => {
                onChange(value);
                setErrorMsg('');
              }}
              placeholder='https://your-awesome-website.com/030524.png'
              trackingId={`${SAMPLING_CAMPAIGNS_TRACKING_ID.PRODUCT_MODAL_INPUT_FIELD}-variations.${index}.imgUrl`}
            />
            <StyledDefaultButton
              onClick={handleAdd}
              trackingId={
                SAMPLING_CAMPAIGNS_TRACKING_ID.PRODUCT_MODAL_CONFIRM_ADD_VARIANT_IMAGE_URL
              }
            >
              Add
            </StyledDefaultButton>
          </InputRow>
        )}
      </FormFieldController>
      {errorMsg && (
        <ErrorText>
          <StyledIcon name='error' />
          <CaptionErrorText>{errorMsg}</CaptionErrorText>
        </ErrorText>
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacings[2]};
`;

const InputRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${({ theme }) => theme.echo.spacing.xxs};
`;

const StyledTextInput = styled(TextInput)`
  flex: 1;
`;

const StyledDefaultButton = styled(DefaultButton)`
  flex-shrink: 0;
`;

const ErrorText = styled.div`
  display: flex;
  align-items: center;
`;

const StyledIcon = styled(Icon)`
  margin-right: ${({ theme }) => theme.spacings[1]};
  color: ${({ theme }) => theme.colors.aliases.error};
`;
